import { apiWithPrefix as api } from '../utils/api';

/**
 * Work From Home Service
 * Handles all WFH-related API calls
 */
export class WFHService {
  /**
   * Get user's WFH requests
   */
  static async getMyRequests() {
    try {
      const response = await api.get('/wfh-requests/my-requests');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch WFH requests');
    }
  }

  /**
   * Get all WFH requests (Admin/HR view)
   */
  static async getAllRequests(filters = {}) {
    try {
      const response = await api.get('/wfh-requests/all', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch all WFH requests');
    }
  }

  /**
   * Create WFH request
   */
  static async createRequest(wfhData) {
    try {
      const response = await api.post('/wfh-requests', wfhData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create WFH request');
    }
  }

  /**
   * Update WFH request status (approve/reject)
   */
  static async updateRequestStatus(id, data) {
    try {
      const response = await api.put(`/wfh-requests/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update WFH request');
    }
  }

  static async updateRequestDetails(id, data) {
    try {
      const response = await api.put(`/wfh-requests/${id}/details`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update WFH request details');
    }
  }

  /**
   * Declare WFH for employee (Admin/HR only)
   */
  static async declareWFH(wfhData) {
    try {
      const response = await api.post('/wfh-requests/declare', wfhData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to declare WFH');
    }
  }

  static async declareWFHBatch(data) {
    try {
      const response = await api.post('/wfh-requests/declare-batch', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to declare WFH batch');
    }
  }

  /**
   * Get WFH statistics
   */
  static async getWFHStats(period = 'month') {
    try {
      const response = await api.get('/wfh-requests/stats', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch WFH statistics');
    }
  }

  /**
   * Get WFH calendar data
   */
  static async getWFHCalendar(year, month) {
    try {
      const response = await api.get('/wfh-requests/calendar', {
        params: { year, month }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch WFH calendar');
    }
  }

  /**
   * Cancel WFH request
   */
  static async cancelRequest(id, reason) {
    try {
      const response = await api.post(`/wfh-requests/${id}/cancel`, { reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel WFH request');
    }
  }

  static async deleteRequest(id) {
    try {
      const response = await api.delete(`/wfh-requests/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete WFH request');
    }
  }

  static async deleteBatch(batchId) {
    try {
      const response = await api.delete(`/wfh-requests/batch/${batchId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete WFH batch');
    }
  }

  /**
   * Get pending WFH requests count
   */
  static async getPendingRequestsCount() {
    try {
      const response = await api.get('/wfh-requests/pending-count');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending WFH requests count');
    }
  }

  /**
   * Export WFH report
   */
  static async exportWFHReport(startDate, endDate, format = 'excel') {
    try {
      const response = await api.get('/wfh-requests/export', {
        params: { startDate, endDate, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export WFH report');
    }
  }

  /**
   * Check WFH eligibility
   */
  static async checkWFHEligibility(date) {
    try {
      const response = await api.post('/wfh-requests/check-eligibility', { date });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check WFH eligibility');
    }
  }

  /**
   * Get WFH policies
   */
  static async getWFHPolicies() {
    try {
      const response = await api.get('/wfh-requests/policies');
      return response.data;
    } catch (error) {
      // Return default policies if API fails
      return {
        maxWFHPerMonth: 8,
        advanceNoticeRequired: 1,
        maxConsecutiveWFH: 3,
        blackoutDates: []
      };
    }
  }

  /**
   * Validate WFH request
   */
  static validateWFHRequest(date, reason) {
    const errors = [];
    
    // Check if date is in the future
    const requestDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (requestDate < today) {
      errors.push('WFH date cannot be in the past');
    }
    
    // Check if it's a weekend
    const dayOfWeek = requestDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      errors.push('WFH cannot be requested for weekends');
    }
    
    // Check reason length
    if (!reason || reason.trim().length < 10) {
      errors.push('Reason must be at least 10 characters');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format WFH status
   */
  static formatStatus(status) {
    const statusMap = {
      'PENDING': { label: 'Pending', color: 'warning' },
      'APPROVED': { label: 'Approved', color: 'success' },
      'REJECTED': { label: 'Rejected', color: 'danger' }
    };
    
    return statusMap[status] || { label: status, color: 'secondary' };
  }
}

export default WFHService;
